import { z } from 'zod';
import { hexColorSchema } from './colors.ts';
import { idSchema } from './content-primitives.ts';
import { jsonValueSchema } from './json.ts';
import { patchToolDefinitions } from './patch-tools.ts';
import { externalSkillNames } from './skill-names.ts';

export const importAssetSchema = z
  .object({
    siteId: idSchema,
    sourceType: z.enum(['Url', 'ChatAttachment']),
    source: z.string(),
  })
  .strict();

export const toolDefinitions = {
  ...patchToolDefinitions,
  generate_color_scale: {
    name: 'generate_color_scale',
    title: 'Generate color scale',
    description:
      'Generate a HEX palette with an exact base, 12 ordered steps, and a contrast text color. Returns { base, steps, contrast } without saving. Step 9 may differ from the base when too close to the background.',
    schema: z.strictObject({ color: hexColorSchema }),
    readOnly: true,
    idempotent: true,
  },
  set_site_color_palette: {
    name: 'set_site_color_palette',
    title: 'Set site color palette',
    description:
      'Generate a primary or secondary color palette from a base color and save it to the site draft, replacing the selected palette’s existing values.',
    schema: z.strictObject({
      siteId: idSchema,
      palette: z.enum(['Primary', 'Secondary']),
      baseColor: hexColorSchema,
    }),
    readOnly: false,
    idempotent: false,
  },
  import_asset: {
    name: 'import_asset',
    title: 'Import site asset',
    description:
      'Import a file as a new site asset. For sourceType Url, source must be the http or https URL to GET. For sourceType ChatAttachment, source must be the ID of an attachment from a chat belonging to the user.',
    schema: importAssetSchema,
    readOnly: false,
    idempotent: false,
  },
  validate_site: {
    name: 'validate_site',
    title: 'Validate site draft',
    description:
      'Check the saved site draft for publication errors and invalid relationships between resources. Returns whether the draft is valid and any errors.',
    schema: z.object({ siteId: idSchema }).strict(),
    readOnly: true,
    idempotent: true,
  },
  api_request: {
    name: 'api_request',
    title: 'Wirechunk API request',
    description:
      'Call a Wirechunk API operation as the current user. Use search_openapi_schema and read_openapi_schema for schemas. The path must start with `/api/`.',
    schema: z
      .object({
        method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
        path: z.string().startsWith('/api/'),
        query: z.record(z.string(), jsonValueSchema).nullable(),
        body: z.record(z.string(), jsonValueSchema).nullable(),
      })
      .strict(),
    readOnly: false,
    idempotent: false,
  },
  search_icons: {
    name: 'search_icons',
    title: 'Search icons',
    description:
      'Search the icons available to Icon blocks and Buttons by name or topic, such as "phone" or "calendar". Returns up to 50 matching icon names as a comma-separated list, best matches first.',
    schema: z.object({ query: z.string().trim() }).strict(),
    readOnly: true,
    idempotent: true,
  },
  send_product_feedback: {
    name: 'send_product_feedback',
    title: 'Send product feedback',
    description:
      'Send feedback to the Wirechunk team, such as something the user needs that Wirechunk does not support or a Wirechunk tool, API, or instruction that is missing, confusing, or broken. Send only after the user agrees. Describe what the user was trying to do and what happened. Wirechunk’s founder sees all feedback and may reach out to the user.',
    schema: z.object({ message: z.string().trim().min(1).max(4000) }).strict(),
    readOnly: false,
    idempotent: false,
  },
  read_openapi_schema: {
    name: 'read_openapi_schema',
    title: 'Read API schema',
    description:
      'Read an inclusive line range from Wirechunk’s OpenAPI YAML. Returns plain YAML without line numbers. Both bounds are required; request at most 500 lines.',
    schema: z
      .object({
        startLine: z.number().int().min(1),
        endLine: z.number().int().min(1),
      })
      .strict(),
    readOnly: true,
    idempotent: true,
  },
  search_openapi_schema: {
    name: 'search_openapi_schema',
    title: 'Search API schema',
    description:
      'Search Wirechunk’s OpenAPI YAML for a case-insensitive literal query. Returns up to 50 matching lines with source line numbers and content. Pass nextCursor with the same query to continue; cursors are one-based source line numbers.',
    schema: z.object({ query: z.string().min(1), cursor: z.number().int().min(1).nullable() }).strict(),
    readOnly: true,
    idempotent: true,
  },
  activate_skill: {
    name: 'activate_skill',
    title: 'Activate skill',
    description: 'Read the contents of a skill',
    schema: z.object({ name: z.enum(externalSkillNames) }).strict(),
    readOnly: true,
    idempotent: true,
  },
} as const;
